// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */

import 'ka-table/style.css';
import {
  KeyboardEvent,
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addLoading, endLoading } from 'redux/slices/loadingSlice';
import { selectUser } from 'redux/slices/userSlice';
import { DbManager } from 'util/DbManager';
import { handleError } from 'util/errorHandler';
import {
  BatchRegistrationLocalData,
  BatchRegistrationWasteUnit,
  ItemType,
} from 'util/Types';
import { DataType, EditingMode, Table, useTable } from 'ka-table';
import { Column } from 'ka-table/models';
import styles from './BatchRegistrationTable.module.scss';
import {
  getUnitLabel,
  mergeClasses,
  normalizeNumber,
  objectFetch,
} from 'util/commons';
import _debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { REGEX_OBJECT } from 'util/ConstantValues';
import Popover from 'components/atoms/Popover';
import Portal from 'components/atoms/Portal';

type BatchRegistrationTableProps = {
  displayWasteUnits: BatchRegistrationWasteUnit[];
  savedLocalData: BatchRegistrationLocalData | undefined;
  setSavedLocalData: React.Dispatch<
    React.SetStateAction<BatchRegistrationLocalData | undefined>
  >;
  totalData: { id: number; totalWeight: number; totalCount: number }[];
  localDataUpdated?: boolean;
};

const ROW_STEP = 22;

const BatchRegistrationTable: React.FC<BatchRegistrationTableProps> = ({
  displayWasteUnits,
  savedLocalData,
  setSavedLocalData,
  localDataUpdated,
  totalData,
}) => {
  const { userId } = useSelector(selectUser);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [tableHeaderHeight, setTableHeaderHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const updateTableHeight = () => {
      if (containerRef.current) {
        setTableHeight(containerRef.current.clientHeight);
      }

      const headerEl = document.querySelector('.ka-thead-row');

      if (headerEl) {
        setTableHeaderHeight(headerEl.clientHeight);
      }
    };

    const debouncedUpdate = _debounce(updateTableHeight, 200);
    debouncedUpdate();
    window.addEventListener('resize', debouncedUpdate);

    return () => {
      window.removeEventListener('resize', debouncedUpdate);
    };
  }, [displayWasteUnits]);

  const getTableData = async () => {
    if (!localDataUpdated) {
      return;
    }

    dispatch(addLoading());

    try {
      const savedUserData = await DbManager.batchRegistrationData.get(userId);

      if (savedUserData?.data) {
        setSavedLocalData(
          displayWasteUnits.reduce((acc, unit) => {
            acc[unit.id] = savedUserData.data[unit.id];

            return acc;
          }, {} as BatchRegistrationLocalData)
        );
      }
    } catch (error) {
      handleError(error);
    } finally {
      dispatch(endLoading());
    }
  };

  const maxRows = useMemo(
    () =>
      Math.max(
        ...displayWasteUnits.map((unit) => {
          const cloned = [...(savedLocalData?.[unit.id] || [])];
          cloned.reverse();
          const firstReversedIndex = cloned.findIndex((val) => val != null);

          if (firstReversedIndex === -1) {
            return 0;
          }

          return cloned.length - firstReversedIndex;
        }),
        0
      ) + ROW_STEP,
    [displayWasteUnits, savedLocalData]
  );

  const tableData = useMemo(() => {
    return Array.from({ length: maxRows }).map((_, rowIndex) => {
      const row: Record<string, number | undefined> = {};
      row['id'] = rowIndex;

      displayWasteUnits.forEach((unit) => {
        row[unit.id] = savedLocalData?.[unit.id]?.[rowIndex];
      });

      return row;
    });
  }, [displayWasteUnits, savedLocalData, maxRows]);

  const tableColumns: Column<Record<string, number | undefined>>[] =
    useMemo(() => {
      if (!displayWasteUnits.length) {
        return [];
      }

      return displayWasteUnits.map<Column<Record<string, number | undefined>>>(
        (unit) => ({
          key: unit.id.toString(),
          title: unit.name,
          dataType: DataType.Number,
          style: { width: '240px' },
          isEditable: true,
          itemType: unit.type,
          convertKg: unit.convertKg,
          unit: unit.unit,
          quantity: unit.quantity,
        })
      );
    }, [displayWasteUnits]);

  useEffect(() => {
    getTableData();
  }, [localDataUpdated, displayWasteUnits]);

  const handleSaveLocalData = async (p: {
    values: (number | undefined)[];
    unitId: number;
  }) => {
    const { values, unitId } = p;
    const cvValues = values.every((val) => val == null) ? [] : values;

    const currData = await DbManager.batchRegistrationData.get(userId);

    setSavedLocalData((prev) => ({
      ...prev,
      [unitId]: values,
    }));

    if (!currData) {
      await DbManager.batchRegistrationData.add({
        userId,
        data: {
          [unitId]: cvValues,
        },
      });

      return;
    }

    await DbManager.batchRegistrationData.update(userId, {
      data: {
        ...currData.data,
        [unitId]: cvValues,
      },
    });
  };

  const table = useTable({
    onDispatch: async (action, tableState) => {
      if (action.type === 'UpdateCellValue') {
        const colKey = action.columnKey;
        const savingData = tableState.data?.map((row) => row[colKey]) ?? [];

        await handleSaveLocalData({
          values: savingData,
          unitId: parseInt(colKey, 10),
        });
      }
    },
  });

  if (!displayWasteUnits.length) {
    return (
      <div
        className={mergeClasses(styles.Container, styles.NoData)}
        ref={containerRef}
      >
        {t('messages.M_008')}
      </div>
    );
  }

  const handleKeydown = (p: {
    e: KeyboardEvent<HTMLElement>;
    column: Column<any>;
    rowKeyValue: any;
  }) => {
    const { e, column, rowKeyValue } = p;

    if (e.key === 'Enter') {
      e.preventDefault();

      // Move to next row's same column editor
      setTimeout(() => {
        table.openEditor(rowKeyValue + 1, column.key);
      }, 200);
    }

    if (e.key === 'Tab') {
      e.preventDefault();

      const colIndex = tableColumns.findIndex((col) => col.key === column.key);

      if (e.shiftKey) {
        // Move to previous cell
        if (colIndex > 0) {
          const leftColKey = tableColumns[colIndex - 1].key;
          table.openEditor(rowKeyValue, leftColKey);
        } else {
          // Move to previous row's last column editor
          if (rowKeyValue > 0) {
            const lastColKey = tableColumns[tableColumns.length - 1].key;
            table.openEditor(rowKeyValue - 1, lastColKey);
          }
        }
      } else {
        // Move to next cell
        if (colIndex < tableColumns.length - 1) {
          const rightColKey = tableColumns[colIndex + 1].key;
          table.openEditor(rowKeyValue, rightColKey);
        } else {
          // Move to next row's first column editor
          if (rowKeyValue < maxRows - 1) {
            const firstColKey = tableColumns[0].key;
            table.openEditor(rowKeyValue + 1, firstColKey);
          }
        }
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();

      if (rowKeyValue < maxRows - 1) {
        table.openEditor(rowKeyValue + 1, column.key);
      }
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();

      if (rowKeyValue > 0) {
        table.openEditor(rowKeyValue - 1, column.key);
      }
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();

      const colIndex = tableColumns.findIndex((col) => col.key === column.key);

      if (colIndex > 0) {
        const leftColKey = tableColumns[colIndex - 1].key;
        table.openEditor(rowKeyValue, leftColKey);
      } else {
        // Move to previous row's last column editor
        if (rowKeyValue > 0) {
          const lastColKey = tableColumns[tableColumns.length - 1].key;
          table.openEditor(rowKeyValue - 1, lastColKey);
        }
      }
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();

      const colIndex = tableColumns.findIndex((col) => col.key === column.key);

      if (colIndex < tableColumns.length - 1) {
        const rightColKey = tableColumns[colIndex + 1].key;
        table.openEditor(rowKeyValue, rightColKey);
      } else {
        // Move to next row's first column editor
        if (rowKeyValue < maxRows - 1) {
          const firstColKey = tableColumns[0].key;
          table.openEditor(rowKeyValue + 1, firstColKey);
        }
      }
    }
  };

  return (
    <div className={styles.Container} ref={containerRef}>
      <Table
        table={table}
        columns={tableColumns}
        data={tableData}
        rowKeyField="id"
        editingMode={EditingMode.Cell}
        virtualScrolling={{
          enabled: true,
        }}
        childComponents={{
          headCell: {
            content: ({ column }) => (
              <>
                <Popover
                  content={column.title}
                  autoGen={{
                    lines: 2,
                    odd: 4,
                  }}
                  positions={['top']}
                  containerStyle={{
                    zIndex: '11',
                    maxWidth: '600px',
                  }}
                >
                  <span>{column.title}</span>
                </Popover>

                {objectFetch(column, 'itemType') === ItemType.FIX ? (
                  <Popover
                    content={`(${objectFetch(
                      column,
                      'convertKg'
                    )}kg/${objectFetch(column, 'quantity')}${getUnitLabel(
                      objectFetch(column, 'unit')
                    )})`}
                    autoGen={{
                      odd: 4,
                    }}
                    positions={['top']}
                    containerStyle={{
                      zIndex: '11',
                      maxWidth: '600px',
                    }}
                  >
                    <div>{`(${objectFetch(column, 'convertKg')}kg/${objectFetch(
                      column,
                      'quantity'
                    )}${getUnitLabel(objectFetch(column, 'unit'))})`}</div>
                  </Popover>
                ) : null}
              </>
            ),
          },
          tableWrapper: {
            elementAttributes: () => ({
              style: {
                maxHeight: tableHeight,
              },
            }),
          },
          dataRow: {
            elementAttributes: ({ rowData }) => ({
              style: {
                // Hide the last row used for spacing and keep virtual scroll working
                display: rowData.id === maxRows - 1 ? 'none' : undefined,
              },
            }),
          },
          cellEditorInput: {
            elementAttributes: ({ column, rowKeyValue }) => ({
              onKeyDown: (e) => handleKeydown({ e, column, rowKeyValue }),
              onWheel: (e) => {
                // Prevent page scroll when scrolling the number input
                e.currentTarget.blur();
              },
            }),
          },
        }}
        format={({ value }) =>
          normalizeNumber({
            value,
          })
        }
        validation={({ value, column }) => {
          const regexp =
            objectFetch(column, 'itemType') === ItemType.FIX
              ? REGEX_OBJECT.number
              : REGEX_OBJECT.fiveDigitsWith2Decimal;

          if (value?.length > 10) {
            return t('emission_input.weight_too_large') || '';
          }

          if (!regexp.test(value) && value != null) {
            return t('messages.M_042') || '';
          }

          return undefined;
        }}
      />

      <Portal selector=".ka-thead">
        <tr className="ka-tr ka-thead-row">
          {totalData.map(({ id, totalWeight }) => (
            <th
              key={id}
              className="ka-thead-cell ka-thead-cell-height ka-thead-fixed ka-thead-background"
              style={{
                backgroundColor: '#86EA5C',
                color: '#000000',
                top: tableHeaderHeight,
              }}
            >
              <Popover
                content={`${t('common.total')}: ${normalizeNumber({
                  value: totalWeight,
                })} kg`}
                autoGen={{
                  odd: 4,
                }}
                containerStyle={{
                  zIndex: '10',
                  maxWidth: '600px',
                }}
                positions={['top']}
              >
                <div className="ka-cell-text">
                  {t('common.total')}: {normalizeNumber({ value: totalWeight })}{' '}
                  kg
                </div>
              </Popover>
            </th>
          ))}
        </tr>
      </Portal>

      <Portal selector=".ka-thead">
        <tr className="ka-tr ka-thead-row">
          {totalData.map(({ id, totalCount }) => (
            <th
              key={id}
              className="ka-thead-cell ka-thead-cell-height ka-thead-fixed ka-thead-background"
              style={{
                backgroundColor: '#D2FDC6',
                color: '#000000',
                top: tableHeaderHeight + 47,
              }}
            >
              <Popover
                content={`${t('common.total')}: ${normalizeNumber({
                  value: totalCount,
                })} ${t('batch_registration.items')}`}
                autoGen={{
                  odd: 4,
                }}
                containerStyle={{
                  zIndex: '10',
                  maxWidth: '600px',
                }}
                positions={['top']}
              >
                <div className="ka-cell-text">
                  {t('common.total')}: {normalizeNumber({ value: totalCount })}{' '}
                  {t('batch_registration.items')}
                </div>
              </Popover>
            </th>
          ))}
        </tr>
      </Portal>
    </div>
  );
};

export default memo(BatchRegistrationTable);
